import type { FormSchema } from '@cli-types/core-engine/schema.js'
import type { PublicFormComponentSourceOptions } from '@cli-types/next-adapter/forms.js'
import { escapeJsx, toPascalCase } from '@cli-utils/text.js'
import { getAllFormSchemaFields } from '@core-engine/schema/field-helpers/form-fields/get-all-form-schema-fields.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { buildDefaultValues } from '@next-adapter/generators/form-pipeline/form-component-shared/build-default-values.js'
import { buildFieldArrayDecls } from '@next-adapter/generators/form-pipeline/form-component-shared/build-field-array-decls.js'
import { buildWatchDecls } from '@next-adapter/generators/form-pipeline/form-component-shared/build-watch-decls.js'
import { buildZodFields } from '@next-adapter/generators/form-pipeline/form-component-shared/build-zod-fields.js'
import { getListFields } from '@next-adapter/generators/form-pipeline/form-component-shared/get-list-fields.js'
import { renderFieldsJSX } from '@next-adapter/generators/form-pipeline/form-component-shared/render-fields-jsx.js'
import { buildComponentSource } from './build-component-source.js'
import { buildStepsConstant } from './build-steps-constant.js'

export function buildMultiStepFormComponentSource(
  schema: FormSchema,
  options: PublicFormComponentSourceOptions = {}
): string {
  const formName = schema.name
  const pascal = toPascalCase(formName)
  const actionImportPath =
    options.actionImportPath ?? resolveSchemaActionImportPath(formName, options.outputNamespace)
  const steps = schema.steps!

  const allFields = getAllFormSchemaFields(schema)
  const zodFields = buildZodFields(allFields)
  const defaults = buildDefaultValues(allFields)

  const listFields = getListFields(allFields)
  const hasListFields = listFields.length > 0
  const { setup: watchSetup } = buildWatchDecls(allFields)

  const rhfImport = hasListFields
    ? `import { Controller, useFieldArray, useForm, type FieldErrors } from 'react-hook-form'`
    : `import { Controller, useForm, type FieldErrors } from 'react-hook-form'`
  const fieldArraySetup = hasListFields ? `\n${buildFieldArrayDecls(listFields)}\n` : ''

  const stepsConst = buildStepsConstant(steps)

  const stepContentBlocks = steps
    .map((step, index) => {
      const fieldsJSX = renderFieldsJSX(step.fields)
      return `          {currentStep === ${index} && (\n            <>\n${fieldsJSX}\n            </>\n          )}`
    })
    .join('\n')

  const submitText = JSON.stringify(schema.submitButtonText || 'Submit')
  const successMessage = escapeJsx(schema.successMessage || 'Form submitted successfully!')
  const redirectUrl = schema.redirectUrl
  const successHandler = redirectUrl
    ? `window.location.href = ${JSON.stringify(redirectUrl)}`
    : 'setSubmitted(true)'

  const content = buildComponentSource({
    pascal,
    rhfImport,
    hasListFields,
    actionImportPath,
    zodFields,
    defaults,
    stepsConst,
    fieldArraySetup,
    watchSetup,
    stepContentBlocks,
    submitText,
    successMessage,
    successHandler
  })

  return content
}
